import type { Modification } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the ingredients whose included-side quantity increased when the
 * included-slot optimizer strictly lowered the modification cost. The host uses
 * the result to surface a "your bowl is priced at its best" message.
 */
export function diffPromotedIngredients(params: Params): IngredientPromotion[] {
  const { before, after } = params;

  const isPriceOptimized = checkIfPriceOptimized({ before, after });

  if (!isPriceOptimized) return [];

  const beforeByIngredient = aggregateByIngredient(before);
  const afterByIngredient = aggregateByIngredient(after);

  const entries = [...afterByIngredient.entries()];

  return entries.flatMap(([ingredientId, afterCounts]) => {
    const beforeIncluded = beforeByIngredient.get(ingredientId)?.includedQuantity ?? 0;

    if (afterCounts.includedQuantity <= beforeIncluded) {
      return [];
    }

    return [
      {
        ingredientId,
        ingredientName: afterCounts.ingredientName,
        includedQuantity: afterCounts.includedQuantity,
        totalQuantity: afterCounts.includedQuantity + afterCounts.extrasQuantity,
      },
    ];
  });
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function checkIfPriceOptimized(params: Params) {
  const { before, after } = params;

  const beforeCost = calculateCost(before);
  const afterCost = calculateCost(after);

  return afterCost < beforeCost;
}

function calculateCost(modifications: Modification[]) {
  return modifications.reduce(
    (totalCost, modification) => totalCost + modification.additionCost * modification.quantity,
    0,
  );
}

function aggregateByIngredient(modifications: Modification[]) {
  return modifications.reduce<Map<string, Aggregated>>((aggregated, modification) => {
    const { ingredientId, ingredient } = modification;
    const ingredientName = ingredient?.name;

    if (!ingredientId || !ingredientName) return aggregated;

    const newEntry = { ingredientName, includedQuantity: 0, extrasQuantity: 0 };
    const current = aggregated.get(ingredientId) ?? newEntry;

    const updated = modification.isExtra
      ? { ...current, extrasQuantity: current.extrasQuantity + modification.quantity }
      : { ...current, includedQuantity: current.includedQuantity + modification.quantity };

    aggregated.set(ingredientId, updated);

    return aggregated;
  }, new Map());
}

// ─── Types ──────────────────────────────────────────────────────────────────

type Params = {
  before: Modification[];
  after: Modification[];
};

export type IngredientPromotion = {
  ingredientId: string;
  ingredientName: string;
  includedQuantity: number;
  totalQuantity: number;
};

type Aggregated = {
  ingredientName: string;
  includedQuantity: number;
  extrasQuantity: number;
};
